/* eslint-disable spire/export-styles */
import { getFocalPointStyles } from "@insite/client-framework/Common/BasicSelectors";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import Zone from "@insite/client-framework/Components/Zone";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContext from "@insite/mobius/GridContainer/GridContext";
import GridContainer from "@bToC/Overrides/Components/CustomGridContainer";
import GridItem, { GridItemStyle, GridWidths } from "@insite/mobius/GridItem";
import { injectCss } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import * as cssLinter from "css";
import * as React from "react";
import styled, { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    isFullWidth = "isFullWidth",
    fullWidthStylingType = "fullWidthStylingType",
    fullWidthColor = "fullWidthColor",
    fullWidthImage = "fullWidthImage",
    fullWidthImageFocalPoint = "fullWidthImageFocalPoint",
    backgroundColor = "backgroundColor",
    backgroundImage = "backgroundImage",
    backgroundImageSize = "backgroundImageSize",
    height = "height",
    width = "width",
    marginTop = "marginTop",
    marginBottom = "marginBottom",
    marginLeft = "marginLeft",
    marginRight = "marginRight",
    paddingTop = "paddingTop",
    paddingBottom = "paddingBottom",
    paddingLeft = "paddingLeft",
    paddingRight = "paddingRight",
    textAlign = "textAlign",
    borderColor = "borderColor",
    borderWidth = "borderWidth",
    borderStyle = "borderStyle",
    marginBorder = "marginBorder",
    flexWrap = "flexWrap",
    customCSS = "customCSS",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.isFullWidth]: boolean;
        [fields.fullWidthStylingType]: string;
        [fields.fullWidthColor]: string;
        [fields.fullWidthImage]: string;
        [fields.fullWidthImageFocalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.marginTop]: string;
        [fields.backgroundColor]: string;
        [fields.marginBottom]: string;
        [fields.marginLeft]: string;
        [fields.marginRight]: string;
        [fields.paddingTop]: string;
        [fields.paddingBottom]: string;
        [fields.paddingLeft]: string;
        [fields.paddingRight]: string;
        [fields.textAlign]: string;
        [fields.borderColor]: string;
        [fields.borderWidth]: string;
        [fields.borderStyle]: string;
        [fields.backgroundImage]: string;
        [fields.backgroundImageSize]: string;
        [fields.height]: string;
        [fields.width]: string;
        [fields.marginBorder]: number;
        [fields.flexWrap]: string;
        [fields.customCSS]: string;
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [6, 6, 3, 3, 3],
    4: [6, 6, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const Row: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: {
            columns,
            gap,
            alignment,
            reflow,
            isFullWidth,
            fullWidthStylingType,
            fullWidthColor,
            fullWidthImage,
            fullWidthImageFocalPoint,
            marginTop,
            marginBottom,
            marginLeft,
            marginRight,
            paddingTop,
            paddingBottom,
            paddingLeft,
            paddingRight,
            backgroundColor,
            textAlign,
            borderColor,
            borderWidth,
            borderStyle,
            backgroundImage,
            height,
            width,
            marginBorder,
            flexWrap,
            customCSS,
        },
        id,
    } = props;

    const gridStyles = {
        css: css`
            width: ${width} !important;
            height: ${height} !important;
            margin-top: ${marginTop};
            margin-bottom: ${marginBottom};
            margin-left: ${marginLeft};
            margin-right: ${marginRight};
            padding-top: ${paddingTop};
            padding-bottom: ${paddingBottom};
            padding-left: ${paddingLeft};
            padding-right: ${paddingRight};
            background-color: ${backgroundColor};
            textalign: ${textAlign};
            background-image: url(${backgroundImage});
            background-size: 100% 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: 100%;
                        `,
                    ],
                    "min",
                )};
        `,
    };
    const gridItemStyles = {
        css: css`
            border-style: ${borderStyle};
            border-color: ${borderColor};
            border-width: ${borderWidth};
        `,
    };

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem
                {...gridItemStyles}
                key={x}
                width={reflow ? convertToResponsive(columns[x]) : columns[x]}
                align={align}
            >
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    const fullWidthBackgroundCss = !isFullWidth
        ? undefined
        : fullWidthStylingType === "solidColor"
          ? css`
                background-color: ${fullWidthColor};
            `
          : fullWidthStylingType === "image"
            ? css`
                  background-image: url("${parseImageUrl(fullWidthImage).url}");
                  background-size: cover;
                  ${getFocalPointStyles(fullWidthImageFocalPoint)}
              `
            : undefined;

    const finalCss = css`
        ${fullWidthBackgroundCss}
        ${customCSS}
    `;

    return (
        <GridContainer
            {...gridStyles}
            gap={gap || 0}
            isFullWidth={isFullWidth}
            marginBorder={marginBorder || 0}
            flexWrap={flexWrap || "wrap"}
        >
            {columnElements}
        </GridContainer>
    );
};

const defaultCustomCss = `& {

}`;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.textAlign,
                displayName: "Text Algin",
                editorTemplate: "DropDownField",
                options: [{ value: "center" }, { value: "right" }, { value: "left" }],
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.borderColor,
                editorTemplate: "ColorPickerField",
                displayName: "Border Color",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.borderWidth,
                editorTemplate: "TextField",
                displayName: "Border Width (px)",
                defaultValue: "0",
            },
            {
                fieldType: "General",
                name: fields.borderStyle,
                displayName: "Border Style",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "None", value: "none" },
                    { displayName: "Dotted", value: "dotted" },
                    { displayName: "Dashed", value: "dashed" },
                    { displayName: "Solid", value: "solid" },
                ],
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.flexWrap,
                displayName: "Border Flex",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Wrap", value: "wrap" },
                    { displayName: "No Wrap", value: "nowrap" },
                ],
                defaultValue: "wrap",
            },
            {
                name: fields.marginBorder,
                displayName: "Margin Between Borders (px)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                fieldType: "Translatable",
                name: fields.backgroundImage,
                editorTemplate: "ImagePickerField",
                displayName: "Row Background image",
                tooltip: "Image Size has to be 330 x 229",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.backgroundImageSize,
                editorTemplate: "TextField",
                displayName: "Row Background Recommended Image Size",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Row Background Color",
                defaultValue: "",
            },
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 2,
            },
            {
                name: fields.isFullWidth,
                displayName: "Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: (_item, _advancedFeaturesEnabled, theme) => !!theme?.content?.isFullWidth,
                sortOrder: 3,
            },
            {
                name: fields.fullWidthStylingType,
                displayName: "Styling",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Solid Color",
                        value: "solidColor",
                    },
                    {
                        displayName: "Image",
                        value: "image",
                    },
                ],
                defaultValue: "solidColor",
                horizontal: true,
                fieldType: "General",
                isVisible: ({ fields: { isFullWidth } }, _advancedFeaturesEnabled, theme) =>
                    !!theme?.content?.isFullWidth && !!isFullWidth,
                sortOrder: 4,
            },
            {
                name: fields.fullWidthColor,
                displayName: "Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "transparent",
                fieldType: "General",
                isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
                    !!theme?.content?.isFullWidth && !!isFullWidth && fullWidthStylingType === "solidColor",
                sortOrder: 5,
            },
            {
                name: fields.fullWidthImage,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
                    !!theme?.content?.isFullWidth && !!isFullWidth && fullWidthStylingType === "image",
                sortOrder: 6,
            },
            {
                name: fields.fullWidthImageFocalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                fieldType: "General",
                hideEmptyOption: true,
                isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
                    !!theme?.content?.isFullWidth && !!isFullWidth && fullWidthStylingType === "image",
                sortOrder: 7,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            {
                name: "customCSS",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                fieldType: "Translatable",
                name: fields.height,
                editorTemplate: "TextField",
                displayName: "Row Height (px/%/rem)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.width,
                editorTemplate: "TextField",
                displayName: "Row Width (px/%/rem)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.marginTop,
                editorTemplate: "TextField",
                displayName: "Margin Top (px/%/rem)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.marginBottom,
                editorTemplate: "TextField",
                displayName: "Margin Bottom (px/%/rem)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.marginLeft,
                editorTemplate: "TextField",
                displayName: "Margin Left (px/%/rem)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.marginRight,
                editorTemplate: "TextField",
                displayName: "Margin Right (px/%/rem)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.paddingTop,
                editorTemplate: "TextField",
                displayName: "Padding Top (px/%/rem)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.paddingBottom,
                editorTemplate: "TextField",
                displayName: "Padding Bottom (px/%/rem)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.paddingLeft,
                editorTemplate: "TextField",
                displayName: "Padding Left (px/%/rem)",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.paddingRight,
                editorTemplate: "TextField",
                displayName: "Padding Right (px/%/rem)",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
